import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "./index.css";

type Operator = "+" | "-" | "×" | "÷" | null;

export function App() {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operator, setOperator] = useState<Operator>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === "0" ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay("0.");
      setWaitingForOperand(false);
      return;
    }
    if (!display.includes(".")) {
      setDisplay(display + ".");
    }
  };

  const clear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(false);
  };

  const toggleSign = () => {
    const value = parseFloat(display);
    if (value !== 0) {
      setDisplay(String(-value));
    }
  };

  const inputPercent = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const calculate = (left: number, right: number, op: Operator): number => {
    switch (op) {
      case "+": return left + right;
      case "-": return left - right;
      case "×": return left * right;
      case "÷": return right !== 0 ? left / right : 0;
      default: return right;
    }
  };

  const handleOperator = (nextOperator: Operator) => {
    const inputValue = parseFloat(display);

    if (previousValue !== null && operator && !waitingForOperand) {
      const result = calculate(previousValue, inputValue, operator);
      const resultStr = parseFloat(result.toFixed(10)).toString();
      setDisplay(resultStr);
      setPreviousValue(result);
    } else {
      setPreviousValue(inputValue);
    }

    setWaitingForOperand(true);
    setOperator(nextOperator);
  };

  const handleEquals = () => {
    if (previousValue === null || operator === null) return;

    const inputValue = parseFloat(display);
    const result = calculate(previousValue, inputValue, operator);
    const resultStr = parseFloat(result.toFixed(10)).toString();
    setDisplay(resultStr);
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(true);
  };

  const formatDisplay = (value: string): string => {
    if (value.includes(".") && !value.endsWith(".")) {
      const [intPart, decPart] = value.split(".");
      const num = parseInt(intPart, 10);
      return num.toLocaleString("en-US") + "." + decPart;
    }
    if (value.endsWith(".")) {
      const num = parseInt(value, 10);
      return num.toLocaleString("en-US") + ".";
    }
    const num = parseFloat(value);
    if (Number.isInteger(num) && !value.includes(".")) {
      return num.toLocaleString("en-US");
    }
    return value;
  };

  const displayFontSize = display.length > 12 ? "text-2xl" : display.length > 8 ? "text-3xl" : "text-5xl";

  const buttons: { label: string; action: () => void; variant?: "default" | "secondary" | "outline"; span?: number }[][] = [
    [
      { label: "AC", action: clear, variant: "secondary" },
      { label: "+/−", action: toggleSign, variant: "secondary" },
      { label: "%", action: inputPercent, variant: "secondary" },
      { label: "÷", action: () => handleOperator("÷"), variant: operator === "÷" && waitingForOperand ? "default" : "outline" },
    ],
    [
      { label: "7", action: () => inputDigit("7") },
      { label: "8", action: () => inputDigit("8") },
      { label: "9", action: () => inputDigit("9") },
      { label: "×", action: () => handleOperator("×"), variant: operator === "×" && waitingForOperand ? "default" : "outline" },
    ],
    [
      { label: "4", action: () => inputDigit("4") },
      { label: "5", action: () => inputDigit("5") },
      { label: "6", action: () => inputDigit("6") },
      { label: "-", action: () => handleOperator("-"), variant: operator === "-" && waitingForOperand ? "default" : "outline" },
    ],
    [
      { label: "1", action: () => inputDigit("1") },
      { label: "2", action: () => inputDigit("2") },
      { label: "3", action: () => inputDigit("3") },
      { label: "+", action: () => handleOperator("+"), variant: operator === "+" && waitingForOperand ? "default" : "outline" },
    ],
    [
      { label: "0", action: () => inputDigit("0"), span: 2 },
      { label: ".", action: inputDecimal },
      { label: "=", action: handleEquals, variant: "default" },
    ],
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-xs shadow-2xl border-muted">
        <CardContent className="p-4">
          {/* Display */}
          <div className="bg-muted/50 rounded-lg p-4 mb-4 min-h-[80px] flex items-end justify-end overflow-hidden">
            <span className={`${displayFontSize} font-light tracking-tight text-foreground truncate`}>
              {formatDisplay(display)}
            </span>
          </div>

          {/* Button Grid */}
          <div className="grid grid-cols-4 gap-2">
            {buttons.map((row, rowIndex) =>
              row.map((btn) => (
                <Button
                  key={`${rowIndex}-${btn.label}`}
                  variant={btn.variant || "ghost"}
                  className={`h-14 text-lg font-medium ${
                    btn.span === 2 ? "col-span-2" : ""
                  } ${
                    !btn.variant ? "bg-muted/30 hover:bg-muted/60" : ""
                  }`}
                  onClick={btn.action}
                >
                  {btn.label}
                </Button>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default App;
